import * as Sentry from '@sentry/browser'
import { GraphQLError } from 'graphql'
import { Operation } from '@apollo/client'
import { ErrorResponse } from '@apollo/client/link/error'
import config from '#src/config'

/**
 * Sentry doesn't have a default export so we'd need to desonstruct the object each time and
 * have some annoying namespace reservation. This small service allows for a single import.
 *
 * See:
 * https://github.com/getsentry/sentry-javascript/blob/master/packages/browser/src/index.ts
 */
interface LogContext {
  // Sentry types this as 'any' themselves
  [key: string]: any
}

/**
 * Log an error message (or full Error) to Sentry and the console
 */
export function captureException(message: Error, context?: LogContext) {
  if (config.get('sentry.enabled')) {
    Sentry.withScope(scope => {
      scope.setContext('context', context || null)

      Sentry.captureException(message)
    })
  }

  console.error(message, context)
}

/**
 * Log a generic message to Sentry and the console
 */
export function captureMessage(message: string, context?: LogContext) {
  if (config.get('sentry.enabled')) {
    Sentry.withScope(scope => {
      scope.setContext('context', context || null)

      Sentry.captureMessage(message)
    })
  }

  console.log(message, context)
}

/**
 * Network errors include server errors, parsing errors, and any other protocol-level errors.
 *
 * See: https://www.apollographql.com/docs/link/links/error/#error-categorization
 */
export function captureNetworkError(
  error: NonNullable<ErrorResponse['networkError']>,
  operation: Operation
) {
  // Identity mismatch errors occur when the Apollo cache has a stale identity compared to
  //   the JWT in the auth cookie. These are expected, to some extent, during various
  //   impersonation flows.
  const isIdentityMismatchError = error.name === 'IdentityMismatchError'

  if (config.get('sentry.enabled')) {
    Sentry.withScope(scope => {
      // Include the operation name as context
      scope.setContext('operation', {
        name: operation.operationName
      })

      // And also as a tag. By including it as a tag, we can see the breakdown of which operations
      //   fail at the network level most often (in case operations are failing because they have
      //   large response sizes, for example)
      scope.setTag('operation', operation.operationName)

      // Identity mismatch errors are less concerning than other errors, and can be grouped together
      if (isIdentityMismatchError) {
        scope.setFingerprint([error.name])
        scope.setLevel(Sentry.Severity.Warning)
      }

      Sentry.captureException(error)
    })
  }

  console.error(error, operation)
}

/**
 * GraphQL errors are returned from the GraphQL endpoint (i.e. our own API business logic)
 *
 * See: https://www.apollographql.com/docs/link/links/error/#error-categorization
 */
export function captureGraphQLError(gqlError: GraphQLError, operation: Operation) {
  // GraphQL errors fall into two categories as far as the client is concerned: authorization errors,
  //   and everything else.
  const isAuthError = gqlError.message.includes('Authorization failure')

  // The GraphQLErrors surfaced by `apollo-link-error` do not actually extend the native Error type.
  //   We create a real Error so Sentry recognizes it. This also gives us a stack trace although
  //   those aren't particularly useful since the stack will be the same for all GraphQL errors.
  //
  // Additionally, since the raw error messages are obfuscated in production we include the best
  //   thing we have available to us: the name of the failed GraphQL operation.
  const err = new Error(`${operation.operationName}: ${gqlError.message}`)

  if (isAuthError) {
    err.name = 'AuthorizationError'
  } else if (gqlError.extensions?.classification) {
    err.name = gqlError.extensions.classification
  } else {
    err.name = 'GraphQLError'
  }

  if (config.get('sentry.enabled')) {
    Sentry.withScope(scope => {
      // Include the operation name as context
      scope.setContext('operation', {
        name: operation.operationName
      })

      // And also as a tag. By including it as a tag, we can search for errors by operation and see the breakdown
      //   of a given common error (e.g. authorization errors) by operation
      scope.setTag('operation', operation.operationName)

      // GraphQL errors are grouped by operation name, except for auth errors. We group all auth errors together
      //   since they are generally not operation-specific.
      //
      // See: https://docs.sentry.io/platforms/javascript/data-management/event-grouping/sdk-fingerprinting/
      if (!isAuthError) {
        scope.setFingerprint([operation.operationName])
      }

      // Auth errors are less concerning than other errors
      if (isAuthError) {
        scope.setLevel(Sentry.Severity.Warning)
      }

      Sentry.captureException(err)
    })
  }

  console.error(err, operation)
}

export default { captureMessage, captureException, captureGraphQLError, captureNetworkError }
